import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash.debounce'
import { ArrowDropDown, Clear } from '@mui/icons-material'
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  TextFieldProps,
} from '@mui/material'
import {
  InfiniteScrollListBox,
  createOnScrollHandler,
} from './InfiniteScrollListBox'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseOption = Record<string, any>

type BasePickerFieldProps<T extends BaseOption> = {
  onChange: (newValue: T | null) => void
  value: T | null
  setDisableArrowKeys: Dispatch<SetStateAction<boolean>>
  inputValue: string
  setInputValue: Dispatch<SetStateAction<string>>
  options: T[]
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  searchValue: string
  setSearchValue: Dispatch<SetStateAction<string>>
  enabled?: boolean
  setEnabled?: Dispatch<SetStateAction<boolean>>
  hasNextPage?: boolean
  fetchNextPage?: () => void
  isLoading?: boolean
  isFetching?: boolean
  isInTable?: boolean
  openOnFocus?: boolean
  suggestedSearches?: [string, string][]
} & Omit<TextFieldProps, 'onChange' | 'value'> &
  Pick<
    AutocompleteProps<T, false, true | false, false, 'div'>,
    | 'isOptionEqualToValue'
    | 'getOptionLabel'
    | 'getOptionKey'
    | 'noOptionsText'
    | 'renderOption'
    | 'groupBy'
  >

export default function BasePickerField<T extends BaseOption>({
  value,
  setDisableArrowKeys,
  label,
  inputValue,
  setInputValue,
  onChange,
  options,
  open,
  setOpen,
  searchValue,
  setSearchValue,
  enabled,
  setEnabled,
  hasNextPage,
  fetchNextPage,
  isLoading,
  isFetching,
  suggestedSearches,
  openOnFocus = false,
  noOptionsText = 'No options',
  isInTable,
  isOptionEqualToValue,
  getOptionLabel,
  getOptionKey,
  groupBy,
  renderOption,
  ...props
}: BasePickerFieldProps<T>) {
  const handleOpen = (e: React.SyntheticEvent<Element, Event>) => {
    if (
      e.type !== 'mousedown' &&
      (e as unknown as KeyboardEvent).key !== 'ArrowDown' &&
      (e as unknown as KeyboardEvent).key !== 'ArrowUp'
    ) {
      setOpen(true)
      if (enabled === false && setEnabled) {
        setEnabled(true) // We fetch the first time the dialog opens
      }
      setDisableArrowKeys(true)
    }
  }

  const clearingInInputRef = useRef(false)
  const popperScrollRef = useRef(0)

  const onScroll = useMemo(
    () =>
      createOnScrollHandler(
        hasNextPage,
        fetchNextPage,
        popperScrollRef,
        isFetching,
      ),
    [hasNextPage, fetchNextPage, popperScrollRef, isFetching],
  )

  const debouncedUpdateValue = useMemo(
    () =>
      debounce((newValue: string) => {
        setSearchValue(newValue)
      }, 200),
    [setSearchValue],
  )

  // Debounce updating the search value
  useEffect(() => {
    if (inputValue !== searchValue) {
      debouncedUpdateValue(inputValue)
    }
  }, [debouncedUpdateValue, inputValue, searchValue])

  return (
    <Autocomplete<T, false, true | false>
      disabled={props.disabled}
      fullWidth
      openOnFocus={openOnFocus}
      clearIcon={<Clear fontSize="inherit" />}
      popupIcon={<ArrowDropDown fontSize="inherit" />}
      loading={isLoading}
      value={value}
      options={options || []}
      disableClearable
      onChange={(_event, newValue, reason) => {
        // If the user clears the input by using the keyboard, we don't want to
        // make a call yet because they might be typing a new value
        if (!newValue && reason === 'clear' && clearingInInputRef.current) {
          clearingInInputRef.current = false
        } else {
          onChange(newValue)
        }
      }}
      open={open}
      onOpen={handleOpen}
      onClose={() => {
        setOpen(false)
        setDisableArrowKeys(false)
        setSearchValue('')
      }}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      groupBy={groupBy}
      renderOption={renderOption}
      onInputChange={(_event, newInputValue, reason) => {
        if (!newInputValue && reason === 'input') {
          clearingInInputRef.current = true
        }
        setInputValue(newInputValue)
      }}
      filterOptions={(x) => x}
      renderInput={(params) => {
        return (
          <TextField
            {...props}
            {...params}
            label={label}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                ...props.inputProps,
                style: {
                  ...params.inputProps.style,
                  ...props.inputProps?.style,
                  fontStyle:
                    value?.id === 'None' || value?.entity_id === 'None'
                      ? 'italic'
                      : undefined,
                  color:
                    value?.id === 'None' || value?.entity_id === 'None'
                      ? 'gray'
                      : undefined,
                },
              },
              input: {
                ...params.InputProps,
                ...props.InputProps,
              },
              inputLabel: {
                ...params.InputLabelProps,
                ...props.InputLabelProps,
              },
            }}
          />
        )
      }}
      noOptionsText={noOptionsText}
      slotProps={{
        clearIndicator: {
          size: isInTable ? 'small' : undefined,
        },
        popupIndicator: {
          size: isInTable ? 'small' : undefined,
        },
        popper: {
          sx: {
            minWidth: 'fit-content',
            width: 'max-content',
            zIndex: (theme) => theme.zIndex.modal + 2,
            gap: 0,
          },
        },
        listbox: {
          onScroll,
          sx: {
            mt: -1,
            pb: 0,
            maxHeight: 'min(40vh, 500px)', // 40vh matches the default max height of the autocomplete, so list resizes when window resizes
          },
          component: InfiniteScrollListBox,
          // @ts-expect-error -- Not sure how to type this since its so deeply nested
          hasInputValue: !!inputValue,
          popperScrollRef,
          suggestedSearches,
          setInputValue,
          hasNextPage,
          fetchNextPage,
          isFetching,
          optionsLength: options.length,
        },
      }}
    />
  )
}
